import { useCallback, useState } from "react";
import { toast } from "sonner";
import { AIChatService } from "@/services/api/AIChatService";
import { DocumentTextService } from "@/services/knowledgebase/DocumentTextService";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { useUpdateDocument } from "@/hooks/documents/useDocumentMutations";

export interface UseDocumentSummaryResult {
  summary: string | null;
  answers: string[];
  topics: string[];
  summarisedAt: string | null;
  isSummarising: boolean;
  error: string | null;
  summarise: () => Promise<void>;
}

/**
 * On-demand summarisation for a single knowledge-base document.
 *
 * Reads the document's extracted text via the existing `DocumentTextService`,
 * sends it to the existing `AIChatService.summarizeDocument`, and persists the
 * result onto the document's `metadata` via the existing `useUpdateDocument`
 * mutation (which merges metadata rather than replacing it). The summary is
 * also mirrored into `metadata.description`, the field the document table
 * already renders.
 *
 * Never writes a placeholder or fabricated summary — a document with no
 * readable text (still parsing) or a failed model call surfaces a real error
 * instead.
 */
export interface UseSummariseDocumentsResult {
  /** How many documents have finished in the current run. */
  completed: number;
  /** How many documents the current run set out to read. 0 when idle. */
  total: number;
  isSummarising: boolean;
  /** Reads every supplied document that has no summary yet, one at a time. */
  summariseAll: (documents: DocumentWithMetadata[]) => Promise<void>;
}

/**
 * How many documents are read at once. High enough that a small library is
 * done in roughly one call's time, low enough not to burst the model endpoint.
 */
const SUMMARISE_CONCURRENCY = 4;

/** True when a document has already been read and carries a usable summary. */
export const hasSummary = (document: DocumentWithMetadata): boolean =>
  typeof document.metadata?.summary === "string" && document.metadata.summary.trim().length > 0;

export const useDocumentSummary = (document: DocumentWithMetadata | null): UseDocumentSummaryResult => {
  const updateDocumentMutation = useUpdateDocument();
  const [isSummarising, setIsSummarising] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summary = (document?.metadata?.summary as string | undefined) ?? null;
  const answers = Array.isArray(document?.metadata?.answers) ? (document!.metadata!.answers as string[]) : [];
  const topics = Array.isArray(document?.metadata?.topics) ? (document!.metadata!.topics as string[]) : [];
  const summarisedAt = (document?.metadata?.summarised_at as string | undefined) ?? null;

  const summarise = useCallback(async () => {
    if (!document) return;

    setError(null);
    setIsSummarising(true);

    try {
      const { text } = await DocumentTextService.getText(document);

      if (!text || !text.trim()) {
        throw new Error(
          "This document doesn't have readable text yet — it may still be parsing. Try again once it finishes.",
        );
      }

      const result = await AIChatService.summarizeDocument({
        text,
        documentName: document.name,
      });

      await updateDocumentMutation.mutateAsync({
        document,
        updates: {
          metadata: {
            summary: result.summary,
            description: result.summary,
            answers: result.answers,
            topics: result.topics,
            summarised_at: new Date().toISOString(),
          },
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to summarise this document.";
      setError(message);
      toast.error(message);
    } finally {
      setIsSummarising(false);
    }
  }, [document, updateDocumentMutation]);

  return { summary, answers, topics, summarisedAt, isSummarising, error, summarise };
};

/**
 * Reads a whole library in one action.
 *
 * Runs the same steps as {@link useDocumentSummary} — extract text, summarise,
 * merge onto `metadata` — across every document that has no summary yet, so one
 * click takes a page from "nothing but filenames" to a full list of what the
 * Genie can answer.
 *
 * Runs `SUMMARISE_CONCURRENCY` documents at a time. Reading a library one
 * document at a time made the wait scale with library size and the page showed
 * nothing until the last one landed. Each result is written as soon as it
 * arrives, so the list fills in progressively rather than all at the end.
 */
export const useSummariseDocuments = (): UseSummariseDocumentsResult => {
  const updateDocumentMutation = useUpdateDocument();
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [isSummarising, setIsSummarising] = useState(false);

  const summariseAll = useCallback(
    async (documents: DocumentWithMetadata[]) => {
      const pending = documents.filter((document) => !hasSummary(document));
      if (pending.length === 0) return;

      setIsSummarising(true);
      setTotal(pending.length);
      setCompleted(0);

      let failures = 0;
      const queue = [...pending];

      const readOne = async (document: DocumentWithMetadata) => {
        try {
          const { text } = await DocumentTextService.getText(document);
          if (!text || !text.trim()) throw new Error(`${document.name} has no readable text yet.`);

          const result = await AIChatService.summarizeDocument({
            text,
            documentName: document.name,
          });

          await updateDocumentMutation.mutateAsync({
            document,
            updates: {
              metadata: {
                summary: result.summary,
                description: result.summary,
                answers: result.answers,
                topics: result.topics,
                summarised_at: new Date().toISOString(),
              },
            },
          });
        } catch {
          // One unreadable source must not abandon the rest of the library.
          failures += 1;
        } finally {
          setCompleted((current) => current + 1);
        }
      };

      // Bounded concurrency: each worker pulls the next document off the shared
      // queue, so a slow source delays only itself rather than the whole run.
      const workers = Array.from(
        { length: Math.min(SUMMARISE_CONCURRENCY, queue.length) },
        async () => {
          for (let next = queue.shift(); next; next = queue.shift()) {
            await readOne(next);
          }
        },
      );

      await Promise.all(workers);

      setIsSummarising(false);
      setTotal(0);
      setCompleted(0);

      const read = pending.length - failures;
      if (failures === 0) {
        toast.success(`Read ${read} ${read === 1 ? "source" : "sources"}.`);
      } else if (read === 0) {
        toast.error("None of these sources could be read yet. They may still be processing.");
      } else {
        toast.warning(`Read ${read} of ${pending.length} sources. The rest may still be processing.`);
      }
    },
    [updateDocumentMutation],
  );

  return { completed, total, isSummarising, summariseAll };
};
